import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  PhoneCall,
  ShieldCheck,
  Sparkles,
  Stethoscope,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FloatingActions } from "@/components/FloatingActions";
import { DoctorPortrait } from "@/components/DoctorPortrait";
import { useMergedDoctors } from "@/lib/use-merged-doctors";
import { notifyForm } from "@/lib/notifyForm";
import type { MergedDoctor } from "@/lib/doctor-merge";

export const Route = createFileRoute("/assessment")({
  head: () => ({
    meta: [
      { title: "Do You Really Need Surgery? — Free Assessment | Healic Gurgaon" },
      {
        name: "description",
        content:
          "Take a 2-minute assessment and get a personalised consultation from Healic Multispeciality Hospital's specialist doctors in Sohna Road, Gurgaon.",
      },
      { property: "og:title", content: "Do You Really Need Surgery? — Free Healic Assessment" },
      {
        property: "og:description",
        content:
          "Answer a few questions and our Gurgaon specialists will guide you on whether surgery is truly needed.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://healic.in/assessment" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Do You Really Need Surgery? — Healic Gurgaon" },
      {
        name: "twitter:description",
        content:
          "Free 2-minute assessment with a personalised specialist recommendation from Healic Gurgaon.",
      },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/assessment" }],
  }),
  component: AssessmentPage,
});

type ConcernKey =
  | "joint"
  | "gynae"
  | "gi"
  | "ent"
  | "eye"
  | "general"
  | "heart"
  | "surgery";

const concernMap: Record<
  ConcernKey,
  { label: string; slug: string; blurb: string; icon: string }
> = {
  joint: {
    label: "Joint, bone or knee pain",
    slug: "dr-arpan-nag-orthopedics-sohna-road-gurugram",
    blurb: "Orthopedic Surgeon — specialises in joint pain, fractures and knee care.",
    icon: "🦴",
  },
  gynae: {
    label: "Women's health / gynae concern",
    slug: "dr-anupama-gonjhu-gynaecologist-gurugram",
    blurb: "Obstetrician & Gynaecologist — expert in women's health & laparoscopic care.",
    icon: "🌸",
  },
  gi: {
    label: "Digestive or abdominal issues",
    slug: "rahul-vijayavargiya",
    blurb: "General Physician & Gastroenterologist — treats stomach, liver and GI concerns.",
    icon: "🩺",
  },
  ent: {
    label: "Ear, nose or throat problem",
    slug: "padma-charan-patra",
    blurb: "ENT Specialist — treats sinus, tonsils, hearing and throat conditions.",
    icon: "👂",
  },
  eye: {
    label: "Vision or eye problem",
    slug: "arundhati-kar",
    blurb: "Ophthalmologist — cataract, refractive and general eye care.",
    icon: "👁️",
  },
  surgery: {
    label: "Piles, hernia, gallbladder or lump",
    slug: "akash-singh",
    blurb: "General Surgeon — treats piles, hernia, gallstones, appendix and lumps.",
    icon: "🔬",
  },
  general: {
    label: "General consultation / not sure",
    slug: "mansvi-singh-general-physician-gurgaon",
    blurb: "General Physician — first-line consult for any new symptom.",
    icon: "💊",
  },
  heart: {
    label: "Heart or chest pain",
    slug: "joginder-singh-duhan",
    blurb: "Cardiologist — evaluates chest pain, BP, and heart concerns.",
    icon: "❤️",
  },
};

const durationOptions = [
  { value: "recent", label: "Less than 2 weeks" },
  { value: "ongoing", label: "2 weeks – 3 months" },
  { value: "chronic", label: "More than 3 months" },
] as const;

const impactOptions = [
  { value: "mild", label: "Mild — manageable discomfort" },
  { value: "moderate", label: "Moderate — limits my activities" },
  { value: "severe", label: "Severe — significantly affecting daily life" },
] as const;

const surgeryOptions = [
  { value: "no", label: "No previous surgeries" },
  { value: "yes-related", label: "Yes, related to this issue" },
  { value: "yes-unrelated", label: "Yes, but unrelated" },
] as const;

const ageOptions = [
  { value: "under-18", label: "Under 18" },
  { value: "18-35", label: "18 – 35" },
  { value: "36-55", label: "36 – 55" },
  { value: "56-plus", label: "56 & above" },
] as const;

const genderOptions = [
  { value: "female", label: "Female", icon: "♀️" },
  { value: "male", label: "Male", icon: "♂️" },
  { value: "other", label: "Prefer not to say", icon: "•" },
] as const;

const step1Schema = z.object({
  name: z.string().trim().min(2, "Please enter your full name").max(100),
  phone: z
    .string()
    .trim()
    .regex(/^[6-9]\d{9}$/, "Please enter a valid 10-digit phone number"),
  email: z
    .string()
    .trim()
    .email("Please enter a valid email")
    .max(255)
    .optional()
    .or(z.literal("")),
});

type Step1 = z.infer<typeof step1Schema>;

type Answers = {
  concern: ConcernKey | "";
  duration: string;
  impact: string;
  surgery: string;
  age: string;
  gender: string;
};

function AssessmentPage() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [details, setDetails] = useState<Step1>({ name: "", phone: "", email: "" });
  const [answers, setAnswers] = useState<Answers>({
    concern: "",
    duration: "",
    impact: "",
    surgery: "",
    age: "",
    gender: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const doctors = useMergedDoctors();

  const gurgaonDoctorBySlug = useMemo(() => {
    const map = new Map<string, MergedDoctor>();
    doctors
      .filter((d) => d.locationSlug === "sohna-road-gurugram")
      .forEach((d) => map.set(d.slug, d));
    return map;
  }, [doctors]);

  // Refine concern → doctor slug using gender (e.g. male patient picking "gynae" should route to general)
  const resolvedConcern: ConcernKey = (() => {
    if (!answers.concern) return "general";
    if (answers.concern === "gynae" && answers.gender === "male") return "general";
    return answers.concern;
  })();
  const recommendedSlug = concernMap[resolvedConcern].slug;
  const recommendedDoctor = gurgaonDoctorBySlug.get(recommendedSlug);

  const handleNext = () => {
    const parsed = step1Schema.safeParse(details);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check the form");
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (
      !answers.concern ||
      !answers.duration ||
      !answers.impact ||
      !answers.surgery ||
      !answers.age ||
      !answers.gender
    ) {
      toast.error("Please answer all questions");
      return;
    }
    setSubmitting(true);
    const doctorLabel = recommendedDoctor
      ? `${recommendedDoctor.name} — ${recommendedDoctor.specialty}`
      : concernMap[resolvedConcern].label;
    const ok = await notifyForm({
      formType: "Surgery Assessment",
      source: "Assessment (Gurgaon)",
      fields: [
        { label: "Name", value: details.name },
        { label: "Phone", value: details.phone },
        { label: "Email", value: details.email || "-" },
        { label: "Age group", value: answers.age },
        { label: "Gender", value: answers.gender },
        { label: "Primary concern", value: concernMap[answers.concern].label },
        { label: "Duration", value: answers.duration },
        { label: "Impact", value: answers.impact },
        { label: "Previous surgery", value: answers.surgery },
        { label: "Recommended doctor", value: doctorLabel },
        { label: "Location", value: "Sohna Road, Gurugram" },
      ],
    });
    setSubmitting(false);
    if (!ok) {
      toast.error("Could not submit. Please call +91 95887 95887.");
      return;
    }
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-primary/5 via-background to-background">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto max-w-3xl px-4 py-10 sm:py-14 lg:px-8">
          {/* Hero */}
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              <Sparkles className="h-3 w-3" /> Free Assessment
            </span>
            <h1 className="mt-4 font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl">
              Do you really need a surgery?
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground sm:text-base">
              Take a quick 2-minute assessment and get a personalised consult from Healic
              Multispeciality Hospital's specialists in Sohna&nbsp;Road, Gurgaon.
            </p>
          </div>

          {/* Trust row */}
          <div className="mt-6 grid grid-cols-3 gap-2 text-center text-[11px] font-medium text-muted-foreground sm:text-xs">
            <div className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card px-2 py-3 shadow-soft">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Only Gurgaon specialists
            </div>
            <div className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card px-2 py-3 shadow-soft">
              <ClipboardCheck className="h-4 w-4 text-primary" />
              6 quick questions
            </div>
            <div className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card px-2 py-3 shadow-soft">
              <PhoneCall className="h-4 w-4 text-primary" />
              Callback in 2 hours
            </div>
          </div>

          {/* Card */}
          <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-card shadow-card">
            {/* Progress */}
            <div className="border-b border-border/60 bg-muted/30 px-6 py-4 sm:px-8">
              <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                <span>Step {step === 3 ? 3 : step} of 3</span>
                <span>
                  {step === 1 && "Your details"}
                  {step === 2 && "Health check"}
                  {step === 3 && "Your recommendation"}
                </span>
              </div>
              <div className="mt-3 flex gap-2">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                      step >= n ? "bg-primary" : "bg-primary/15"
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="p-6 sm:p-8">
              {step === 1 && (
                <Step1Form
                  values={details}
                  onChange={setDetails}
                  onNext={handleNext}
                />
              )}
              {step === 2 && (
                <Step2Form
                  answers={answers}
                  onChange={setAnswers}
                  onBack={() => setStep(1)}
                  onSubmit={handleSubmit}
                  submitting={submitting}
                />
              )}
              {step === 3 && (
                <Step3Result
                  name={details.name}
                  doctor={recommendedDoctor}
                  concernKey={resolvedConcern}
                />
              )}
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            🔒 Your information is confidential and used only to arrange your consultation.
          </p>
        </section>
      </main>
      <FloatingActions />
      <SiteFooter />
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-foreground">{label}</label>
      {children}
    </div>
  );
}

function Step1Form({
  values,
  onChange,
  onNext,
}: {
  values: Step1;
  onChange: (v: Step1) => void;
  onNext: () => void;
}) {
  return (
    <div className="space-y-5">
      <h2 className="font-display text-xl font-semibold text-foreground">
        Tell us about yourself
      </h2>
      <Field label="Full name">
        <input
          type="text"
          value={values.name}
          maxLength={100}
          onChange={(e) => onChange({ ...values, name: e.target.value })}
          placeholder="Enter your full name"
          className="h-11 w-full rounded-xl border border-input bg-background px-4 text-sm outline-none ring-primary/40 transition focus:ring-2"
        />
      </Field>
      <Field label="Phone number">
        <input
          type="tel"
          inputMode="numeric"
          value={values.phone}
          maxLength={10}
          onChange={(e) =>
            onChange({ ...values, phone: e.target.value.replace(/\D/g, "") })
          }
          placeholder="10-digit mobile number"
          className="h-11 w-full rounded-xl border border-input bg-background px-4 text-sm outline-none ring-primary/40 transition focus:ring-2"
        />
      </Field>
      <Field label="Email (optional)">
        <input
          type="email"
          value={values.email ?? ""}
          maxLength={255}
          onChange={(e) => onChange({ ...values, email: e.target.value })}
          placeholder="you@email.com"
          className="h-11 w-full rounded-xl border border-input bg-background px-4 text-sm outline-none ring-primary/40 transition focus:ring-2"
        />
      </Field>
      <div className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-xs text-primary">
        📍 Currently available for patients in <strong>Sohna Road, Gurugram</strong>.
      </div>
      <button
        type="button"
        onClick={onNext}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-cta transition hover:opacity-90"
      >
        Continue <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function OptionGroup<T extends string>({
  name,
  value,
  onChange,
  options,
  columns = 1,
}: {
  name: string;
  value: string;
  onChange: (v: T) => void;
  options: readonly { value: T; label: string; icon?: string }[];
  columns?: 1 | 2;
}) {
  return (
    <div
      className={`grid gap-2 ${columns === 2 ? "sm:grid-cols-2" : ""}`}
      role="radiogroup"
      aria-label={name}
    >
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            type="button"
            key={o.value}
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(o.value)}
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-left text-sm transition ${
              selected
                ? "border-primary bg-primary/5 text-primary shadow-soft"
                : "border-border bg-background text-foreground hover:border-primary/40 hover:bg-muted/40"
            }`}
          >
            {o.icon && <span className="text-lg leading-none">{o.icon}</span>}
            <span className="flex-1 font-medium">{o.label}</span>
            <span
              className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 transition ${
                selected ? "border-primary bg-primary" : "border-border"
              }`}
              aria-hidden
            />
          </button>
        );
      })}
    </div>
  );
}

function Step2Form({
  answers,
  onChange,
  onBack,
  onSubmit,
  submitting,
}: {
  answers: Answers;
  onChange: (a: Answers) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const concernOptions = (Object.keys(concernMap) as ConcernKey[]).map((k) => ({
    value: k,
    label: concernMap[k].label,
    icon: concernMap[k].icon,
  }));
  return (
    <div className="space-y-7">
      <h2 className="font-display text-xl font-semibold text-foreground">
        Tell us about your health
      </h2>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground">
          Your age group
        </p>
        <OptionGroup
          name="age"
          value={answers.age}
          onChange={(v) => onChange({ ...answers, age: v })}
          options={ageOptions}
          columns={2}
        />
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground">
          Your gender
        </p>
        <OptionGroup
          name="gender"
          value={answers.gender}
          onChange={(v) => onChange({ ...answers, gender: v })}
          options={genderOptions}
          columns={2}
        />
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground">
          What's your primary concern?
        </p>
        <OptionGroup
          name="concern"
          value={answers.concern}
          onChange={(v) => onChange({ ...answers, concern: v as ConcernKey })}
          options={concernOptions}
          columns={2}
        />
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground">
          How long have you had this issue?
        </p>
        <OptionGroup
          name="duration"
          value={answers.duration}
          onChange={(v) => onChange({ ...answers, duration: v })}
          options={durationOptions}
        />
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground">
          How is it affecting your daily life?
        </p>
        <OptionGroup
          name="impact"
          value={answers.impact}
          onChange={(v) => onChange({ ...answers, impact: v })}
          options={impactOptions}
        />
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground">
          Have you had any previous surgeries?
        </p>
        <OptionGroup
          name="surgery"
          value={answers.surgery}
          onChange={(v) => onChange({ ...answers, surgery: v })}
          options={surgeryOptions}
        />
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={onSubmit}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground shadow-cta transition hover:opacity-90 disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Getting recommendation…
            </>
          ) : (
            <>
              Get my recommendation <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function Step3Result({
  name,
  doctor,
  concernKey,
}: {
  name: string;
  doctor: MergedDoctor | undefined;
  concernKey: ConcernKey;
}) {
  const info = concernMap[concernKey];
  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/85 p-5 text-primary-foreground shadow-card sm:p-6">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
          <div>
            <h2 className="font-display text-xl font-semibold">
              Thanks{name ? `, ${name.split(" ")[0]}` : ""} — assessment complete!
            </h2>
            <p className="mt-1 text-sm text-primary-foreground/85">
              Based on your answers, here's the specialist we recommend for you at Healic
              Sohna Road, Gurgaon.
            </p>
          </div>
        </div>
      </div>

      {doctor ? (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:gap-6 sm:p-6">
            <DoctorPortrait
              doctor={doctor}
              className="h-32 w-32 shrink-0 self-center overflow-hidden rounded-2xl sm:h-36 sm:w-36"
            />
            <div className="flex-1">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                Recommended
              </span>
              <h3 className="mt-2 font-display text-xl font-semibold text-foreground">
                {doctor.name}
              </h3>
              <p className="text-sm font-semibold text-primary">{doctor.specialty}</p>
              {doctor.experience && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {doctor.experience} · Sohna Road, Gurugram
                </p>
              )}
              <p className="mt-3 text-sm text-muted-foreground">{info.blurb}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/doctors/$slug"
                  params={{ slug: doctor.slug }}
                  className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-muted"
                >
                  View full profile
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground shadow-cta transition hover:opacity-90"
                >
                  <PhoneCall className="h-4 w-4" /> Book consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground shadow-soft">
          <Stethoscope className="mb-2 h-5 w-5 text-primary" />
          Our Gurgaon care team will call you within 2 hours to match you with the right
          specialist.
        </div>
      )}

      <div className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-sm text-primary">
        📞 <strong>Our care team will call you within 2 hours</strong> to confirm your
        consultation and answer your questions.
      </div>
    </div>
  );
}
